import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Config } from "../config";
import { IconButton } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';

function AIVision({ className, control, onComplianceLevelDetected, onAddComment, evidenceDocuments }) {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingDots, setLoadingDots] = useState('');
  const [analysisResults, setAnalysisResults] = useState([]);
  const resultsEndRef = useRef(null);

  // Get files from evidence documents for this control
  const getControlEvidenceFiles = () => {
    return evidenceDocuments
      ?.filter(doc => doc.controls.includes(control.ref))
      .flatMap(doc => ({
        ...doc.files,
        evidenceId: doc.id, // Keep track of which evidence document it belongs to
        description: doc.description // Keep the evidence description
      })) || [];
  };

  useEffect(() => {
    if (isLoading) {
      const interval = setInterval(() => {
        setLoadingDots(dots => dots.length >= 3 ? '' : dots + '.');
      }, 500);
      return () => clearInterval(interval);
    }
  }, [isLoading]);

  const extractComplianceLevel = (text) => {
    if (!text) {
      console.error('No text provided to extractComplianceLevel');
      return null;
    }
    
    const content = String(text).toLowerCase();
    
    // Use regex to match compliance levels with percentages
    if (/no\s*cumple\s*\(0%\)/i.test(content)) {
      return 1;
    }
    if (/cumple\s*parcialmente\s*\(50%\)/i.test(content)) {
      return 2;
    }
    if (/cumple\s*totalmente\s*\(100%\)/i.test(content)) {
      return 3;
    }
    
    // Fallback: match by keywords without percentages
    if (/no\s*cumple/i.test(content) && !/cumple\s*(parcialmente|totalmente)/i.test(content)) {
      return 1;
    }
    if (/cumple\s*parcialmente/i.test(content)) {
      return 2;
    }
    if (/cumple\s*totalmente/i.test(content)) {
      return 3;
    }

    return null;
  };

  const handleAnalyze = async (file) => {
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('control', JSON.stringify(control));

      const response = await axios.post(Config.APIBaseURL + '/analyze_file', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'access_token': `Bearer ${token}`
        },
        withCredentials: true
      });

      let analysisContent = null;
      
      if (response.data && response.data.analysis) {
        analysisContent = response.data.analysis;
        
        const newResult = {
          fileName: file.name,
          content: analysisContent
        };

        setAnalysisResults(prev => [...prev, newResult]);

        const complianceLevel = extractComplianceLevel(analysisContent);
        if (complianceLevel !== null && onComplianceLevelDetected) {
          onComplianceLevelDetected(control.ref, complianceLevel);
        }
      } else {
        setAnalysisResults(prev => [...prev, {
          fileName: file.name,
          content: 'Analysis failed or returned unexpected format.'
        }]);
      }

    } catch (error) {
      console.error('Error analyzing file:', error);
      setAnalysisResults(prev => [...prev, {
        fileName: file.name,
        content: 'Sorry, I encountered an error analyzing this file.'
      }]);
    } finally {
      setIsLoading(false);
      setLoadingDots('');
    }
  };

  const handleAddComment = (result) => {
    if (onAddComment && control) {
      onAddComment(control.ref, result.content);
    }
  };

  return (
    <div className={`flex flex-col h-full max-w-xl mx-auto ${className}`}>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {analysisResults.map((result, index) => (
          <div key={index} className="flex flex-col gap-2">
            <div className="text-sm text-slate-400">{result.fileName}</div>
            <div className="flex items-start gap-2">
              <div className="max-w-[75%] rounded-lg p-3 bg-slate-700 text-slate-200">
                {result.content}
              </div>
              <button
                className="btn p-1 bg-slate-700 hover:bg-slate-600 text-slate-400 hover:text-slate-300" 
                title="Agregar como comentario"
                onClick={() => handleAddComment(result)}
              >
                <svg className="w-3 h-3 fill-current text-white" viewBox="0 0 24 24">
                  <path d="M12 4C11.4477 4 11 4.44772 11 5V11H5C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13H11V19C11 19.5523 11.4477 20 12 20C12.5523 20 13 19.5523 13 19V13H19C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11H13V5C13 4.44772 12.5523 4 12 4Z" />
                </svg>
              </button>
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-slate-700 text-slate-200 rounded-lg p-3">
              Analizando{loadingDots}
            </div>
          </div>
        )}
        <div ref={resultsEndRef} />
      </div>
      
      {/* Evidence Files List */}
      <div className="p-4">
        <div className="text-sm text-slate-400 mb-2">
          Material de Apoyo disponible para análisis:
        </div>
        <div className="space-y-2">
          {getControlEvidenceFiles().map((file, index) => (
            <div key={index} className="flex flex-col space-y-1">
              <div className="text-xs text-slate-500">{file.description}</div>
              <div className="flex items-center justify-between p-2 bg-slate-800 rounded-lg">
                <span className="text-sm text-slate-300 truncate">{file.name}</span>
                <IconButton
                  onClick={() => handleAnalyze(file)}
                  disabled={isLoading}
                  sx={{
                    color: 'rgb(148, 163, 184)',
                    '&:hover': {
                      color: 'rgb(226, 232, 240)',
                      backgroundColor: 'rgba(30, 41, 59, 0.5)'
                    }
                  }}
                >
                  <VisibilityIcon />
                </IconButton>
              </div>
            </div>
          ))}
          {getControlEvidenceFiles().length === 0 && (
            <div className="text-sm text-slate-500 text-center py-4">
              No hay material de apoyo disponible para este control.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AIVision; 